import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { toast } from "react-toastify";

import GatewaySelectionDialog from "../Common/PaymentGatewaySelectionDialog";
import { createOfflineOrderRequest } from "../../redux/OfflineOrder/offlineOrderSlice";
import {
  getPaymentPlatform,
  normalizeGateway,
  savePaymentMeta,
  startPaymentFromOrderResponse,
} from "../Common/PaymentGatewayMethods";
import {
  getAppConfigRequest,
  getBullionRatesRequest,
} from "../../redux/Dashboard/dashboardSlice";
import ReadMore from "react-read-more-read-less";
import {
  createOnlineOrderRequest,
  resetOnlineOrderState,
} from "../../redux/OnlineOrder/onlineOrderSlice";
import { gstHelper } from "../../Utils/GstCalculationResult";
import { useAppTheme } from "../../theme/useAppTheme";
import useCurrency from "../../hooks/useCurrency";

const intervalMap = {
  daily: "DAY",
  weekly: "WEEK",
  monthly: "MONTH",
  yearly: "YEAR",
  day: "DAY",
  week: "WEEK",
  month: "MONTH",
  year: "YEAR",
};

const SIPSummary = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const onlineOrder = useSelector((state) => state.onlineOrder);
  const { appConfig, bullionRates } = useSelector(
    (state) => state.dashboard || {},
  );
  const offlineOrder = useSelector((state) => state.offline_order);

  const sip = state?.sipData || {};
  const metal = state?.metal || {};
  const initialAmount = Number(state?.amount) || 0;
  const sipType = sip?.bullion?._id ? "Metal SIP" : "Money SIP";
  const isMoneySip = !sip?.bullion?._id;

  const paymentGateways = appConfig?.paymentGateways || [];
  const decimalPrecision = appConfig?.decimalPrecision || 2;

  const { customer: fetchedCustomer } = useSelector(
    (state) => state.profile || {},
  );
  const customer = fetchedCustomer || {};

  const customerState = customer?.address?.[0]?.state || "";
  const jewellerState = appConfig?.jewellerState || "";

  const taxtype = appConfig?.taxtype ?? "GST";
  const isSameState =
    customerState?.trim().toLowerCase() === jewellerState?.trim().toLowerCase();
  const [cess, setCess] = useState(0);

  const [sipAmount, setSipAmount] = useState(sip?.maxQty || initialAmount || 0);
  const [months, setMonths] = useState(sip?.maxDuration || 1);
  const [tag, setTag] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [autoDebit, setAutoDebit] = useState(!!sip?.autodebit);

  const [ratePerGram, setRatePerGram] = useState(0);
  const [calculatedGrams, setCalculatedGrams] = useState(0);
  const [metalCost, setMetalCost] = useState(0);
  const [cgst, setCgst] = useState(0);
  const [sgst, setSgst] = useState(0);
  const [tax, setTax] = useState(0);
  const [totalPayable, setTotalPayable] = useState(0);

  const [openGatewayDialog, setOpenGatewayDialog] = useState(false);
  const [selectedGateway, setSelectedGateway] = useState(null);
  const [loading, setLoading] = useState(false);

  const themeColors = useAppTheme();

  const { symbol } = useCurrency();

  useEffect(() => {
    dispatch(getAppConfigRequest());
    dispatch(
      getBullionRatesRequest({ id: `${process.env.REACT_APP_JEWELLER_ID}` }),
    );
  }, [dispatch]);

  const calculateReductionValue = (decimalPlaces) =>
    1 / Math.pow(10, decimalPlaces);

  const calculateCost = (amount, rateWithGst, rateWithoutGst) => {
    if (!appConfig) {
      return {
        grams: 0,
        metalCost: 0,
        cgst: 0,
        sgst: 0,
        tax: 0,
        payableAmount: amount,
      };
    }

    if (!metal?._id && !metal?.id && !sip?.bullion?._id && !sip?.bullion?.id) {
      return {
        grams: 0,
        metalCost: 0,
        cgst: 0,
        sgst: 0,
        tax: 0,
        payableAmount: Number(amount),
      };
    }

    const reductionValue = calculateReductionValue(decimalPrecision);

    if (!rateWithGst || rateWithGst === 0) {
      return {
        grams: 0,
        metalCost: 0,
        cgst: 0,
        sgst: 0,
        tax: 0,
        payableAmount: Number(amount),
      };
    }

    let grams = parseFloat((amount / rateWithGst).toFixed(decimalPrecision));
    if (grams * rateWithGst > amount) grams -= reductionValue;

    const metalCost = parseFloat(
      (grams * rateWithoutGst).toFixed(decimalPrecision),
    );
    const cgst = parseFloat(
      ((metalCost * (appConfig.gst?.cgst || 0)) / 100).toFixed(
        decimalPrecision,
      ),
    );
    const sgst = parseFloat(
      ((metalCost * (appConfig.gst?.sgst || 0)) / 100).toFixed(
        decimalPrecision,
      ),
    );
    const tax = cgst + sgst;
    const payableAmount = metalCost + tax;

    return { grams, metalCost, cgst, sgst, tax, payableAmount };
  };
  useEffect(() => {
    dispatch(resetOnlineOrderState());
  }, []);

  useEffect(() => {
    if (!bullionRates?.length || !appConfig) {
      if (!sip?.bullion || !sip?.bullion?.id) {
        setCalculatedGrams(0);
        setRatePerGram(0);
        setTotalPayable(Number(sipAmount));
      }
      return;
    }

    const matchedRate = bullionRates.find(
      (item) =>
        item.rateType === "Buy" &&
        (item.bullion?.metalName || "").toString().toLowerCase() ===
          (metal?.metalName || sip?.bullion?.metalName || "")
            .toString()
            .toLowerCase() &&
        Number(item.bullion?.purity || item.bullion?.purity) ===
          Number(metal?.purity || sip?.bullion?.purity) &&
        String(item.bullion?.karat || item.bullion?.caret || "") ===
          String(metal?.karat || sip?.bullion?.caret || ""),
    );

    if (matchedRate) {
      const baseRate = Number(matchedRate.ratePerGram);
      const grams = Number((sipAmount / baseRate).toFixed(decimalPrecision));
      const gstResult = gstHelper({
        finalWeight: grams,
        rate: baseRate,
        igst: metal?.tax || 0,
        cgst: metal?.cgst || 0,
        sgst: metal?.sgst || 0,
        cess: metal?.cess || 0,
        taxRate: metal?.tax || 0,
        taxType: taxtype,
        customerState,
        jewellerState,
      });
      console.log("gstResult", gstResult);
      const { cgst, sgst, tax, metalCost, finalAmount, cess } = gstResult;

      setCalculatedGrams(grams.toFixed(decimalPrecision));
      setRatePerGram(baseRate);
      setMetalCost(Number(metalCost));
      setCgst(Number(cgst));
      setSgst(Number(sgst));
      setTax(Number(tax));
      setCess(Number(cess));
      setTotalPayable(Number(finalAmount));
    } else {
      if (!sip?.bullion || !sip?.bullion?.id) {
        setCalculatedGrams(0);
        setRatePerGram(0);
        setMetalCost(0);
        setCgst(0);
        setSgst(0);
        setTax(0);
        setCess(0);
        setTotalPayable(Number(sipAmount));
      } else {
        setCalculatedGrams(0);
        setRatePerGram(0);
        setTotalPayable(0);
      }
    }
  }, [bullionRates, metal, sipAmount, appConfig]);

  const paymentModeEnabled = appConfig?.sipPaymentModeEnabled || {};

  const metalColor =
    metal.metalName === "Gold"
      ? "linear-gradient(135deg, #FFD700, #DAA520)"
      : metal.metalName === "Silver"
        ? "linear-gradient(135deg, #C0C0C0, #E0E0E0)"
        : "linear-gradient(135deg, #B0BEC5, #ECEFF1)";

  const handlePayOnline = async () => {
    if (
      Number(sipAmount) < Number(sip?.minQty || 0) ||
      Number(sipAmount) > Number(sip?.maxQty || Infinity)
    ) {
      toast.error(`Amount must be between ${symbol}${sip?.minQty} and ${symbol}${sip?.maxQty}`);
      return;
    }
    if (sip?.discalimer && sip.discalimer.length > 0 && !agreed) {
      toast.error("Please accept terms & conditions to continue");
      return;
    }

    if (!paymentGateways.length) {
      toast.warn("No payment gateways configured!");
      return;
    }
    setLoading(true);
    if (paymentGateways.length === 1) {
      const gateway = normalizeGateway(paymentGateways[0]);
      setSelectedGateway(gateway);
      await createOrderAndPay(gateway);
    } else {
      setLoading(false);
      setOpenGatewayDialog(true);
    }
  };

  const handleGatewaySelect = async (gateway) => {
    setOpenGatewayDialog(false);
    if (!gateway) return;
    const normalized = normalizeGateway(gateway);
    setSelectedGateway(normalized);
    await createOrderAndPay(normalized);
  };

  const normalizeInterval = (clientValue) =>
    intervalMap[(clientValue || "").toLowerCase()] || clientValue || "";

  useEffect(() => {
    if (onlineOrder.error) {
      toast.error(
        onlineOrder.error?.message ||
          onlineOrder.error ||
          "Order creation failed",
      );
      setLoading(false);
    }
  }, [onlineOrder.error]);

  useEffect(() => {
    if (!onlineOrder.data || !selectedGateway) return;

    const pay = async () => {
      setLoading(true);
      await startPaymentFromOrderResponse(
        onlineOrder.data,
        selectedGateway,
        { autodebit: autoDebit },
      );
      setLoading(false);
    };

    pay();
  }, [onlineOrder.data, selectedGateway, autoDebit]);

  const createOrderAndPay = async (gateway) => {
    try {
      setLoading(true);
      const payLoad = {
        weight: isMoneySip ? 0 : Number(calculatedGrams),
        metalName: isMoneySip
          ? "Money"
          : metal?.metalName || sip?.bullion?.metalName,
        orderType: "Sip",
        final_amount: Number(totalPayable),
        tax: isMoneySip
          ? 0
          : Number((totalPayable - (metalCost || 0)).toFixed(decimalPrecision)),
        noOfMonths: Number(months),
        Id: "",
        sipDay: Number(sip?.installmentDate || 0),
        buyType: "Amount",
        purity: isMoneySip ? 0 : metal?.purity || sip?.bullion?.purity || 0,
        transactionMode: normalizeGateway(gateway),
        platform: getPaymentPlatform(),
        installment_amount: sipAmount,
        planid: sip?._id || sip?.moneysipplan?._id || "",
        moneysip: !(sip?.bullion && sip?.bullion?._id),
        tag,
        x_api_version: "2022-09-01",
        jeweller: process.env.REACT_APP_JEWELLER_ID,
        autodebit: Boolean(autoDebit),
        interval: normalizeInterval(sip?.interval || "monthly"),
      };
      savePaymentMeta(
        {
          grams: Number(calculatedGrams),
          metal: metal && metal.metalName ? metal : { metalName: "Money" },
          amount: Number(totalPayable),
          totalAmount: totalPayable,
          tax,
          selectedGateway: normalizeGateway(gateway),
          orderType: payLoad.orderType,
          buyType: payLoad.buyType,
          planid: payLoad.planid,
          moneysip: payLoad.moneysip,
          autodebit: Boolean(autoDebit),
        },
        { replace: true },
      );
      dispatch(resetOnlineOrderState());
      dispatch(createOnlineOrderRequest(payLoad));
    } catch (err) {
      console.error(err);
      toast.error("Failed to create order");
      setLoading(false);
    }
  };

  const handleOfflineSip = () => {
    if (
      Number(sipAmount) < Number(sip?.minQty || 0) ||
      Number(sipAmount) > Number(sip?.maxQty || Infinity)
    ) {
      toast.error(
        `Amount must be between ${symbol}${sip?.minQty} and ${symbol}${sip?.maxQty}`,
      );
      return;
    }
    if (sip?.discalimer && sip.discalimer.length > 0 && !agreed) {
      toast.error("Please accept terms & conditions to continue");
      return;
    }

    const planid = sip?._id || sip?.moneysipplan?._id || "";
    if (!planid) {
      toast.error("SIP plan is missing");
      return;
    }

    navigate("/offlineSipPayment", {
      state: {
        ...state,
        offlineSIPOrderData: {
          metalName: isMoneySip
            ? "Money"
            : metal?.metalName || sip?.bullion?.metalName,
          karat: isMoneySip
            ? ""
            : metal?.karat || sip?.bullion?.caret || sip?.bullion?.karat || "",
          purity: isMoneySip
            ? 0
            : Number(metal?.purity || sip?.bullion?.purity || 0),
          weight: isMoneySip ? 0 : Number(calculatedGrams) || 0,
          tax: isMoneySip
            ? 0
            : Number(
                (totalPayable - (metalCost || 0)).toFixed(decimalPrecision),
              ),
          final_amount: Number(totalPayable) || 0,
          installment_amount: Number(sipAmount) || 0,
          noOfMonths: Number(months) || 0,
          bullion: sip?.bullion,
          planid,
          moneysipplan: sip,
          isInstallment: false,
          Id: "",
          sipDay: Number(sip?.installmentDate || sip?.sipDay || 0),
          orderType: "Installment",
          tag,
        },
      },
    });
  };

  const discountedAmountDisplay = () => {
    const original = sip?.bullion?.id
      ? Number(totalPayable)
      : Number(sipAmount);
    const percentage = Number(sip?.percentage || 0);
    if (percentage > 0) {
      const discounted = Number(
        (original - (percentage * original) / 100).toFixed(2),
      );
      return {
        original,
        percentage,
        discounted,
      };
    }
    return null;
  };

  const discount = discountedAmountDisplay();

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        py: 3,
        background: themeColors.scaffoldGradientColors,
      }}
    >
      <Card
        sx={{
          width: "50vw",
          borderRadius: "25px",
          boxShadow: "0 10px 25px rgba(0,0,0,0.08)",
          background: themeColors.scaffoldGradientColors,
          color: themeColors.text,
          border: `1px solid ${themeColors.scaffold}`,
          p: 3,
        }}
      >
        <CardContent>
          <Typography
            variant="h5"
            sx={{
              textAlign: "center",
              fontWeight: "bold",
              color: themeColors.primary,
              mb: 3,
            }}
          >
            Start New SIP
          </Typography>

          <Box
            sx={{
              p: 2,
              borderRadius: "15px",
              background: themeColors.mediumContainer,
              border: `1px solid ${themeColors.scaffold}`,
              color: themeColors.text,
              mb: 3,
            }}
          >
            <Typography sx={{ fontWeight: "bold", fontSize: "18px" }}>
              {sip?.name}
            </Typography>
            <Typography sx={{ opacity: 0.8, fontSize: "14px" }}>
              {sipType}{" "}
            </Typography>
            <Typography sx={{ mt: 1, fontSize: "14px" }}>
              • Duration Range: {sip?.minDuration} - {sip?.maxDuration}{" "}
              {sip?.durationType || "Months"}
            </Typography>
            <Typography sx={{ fontSize: "14px" }}>
              • Investment Range: {symbol}{sip?.minQty} – {symbol}{sip?.maxQty}
            </Typography>
          </Box>

          <Box
            sx={{
              background: metalColor,
              borderRadius: "15px",
              textAlign: "center",
              p: 2,
              mb: 3,
              border: `1px solid ${themeColors.scaffold}`,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: "bold", color: "#fff" }}>
              {metal?.metalName || sip?.bullion?.metalName || "Money"}-{" "}
              {metal?.karat || sip?.bullion?.caret || ""}k -{" "}
              {metal?.purity || sip?.bullion?.purity || ""}
            </Typography>
          </Box>

          {/* SIP Amount */}
          <Box sx={{ mt: 2 }}>
            <Typography sx={{ fontWeight: "bold", mb: 1 }}>
              SIP Amount
            </Typography>
            <TextField
              fullWidth
              type="number"
              value={sipAmount}
              onChange={(e) => setSipAmount(e.target.value)}
              inputProps={{ min: sip.minQty, max: sip.maxQty }}
              sx={{
                borderRadius: "10px",
                "& .MuiOutlinedInput-root": {
                  background: themeColors.mediumContainer,
                  color: themeColors.text,
                },
                "& .MuiInputLabel-root": {
                  color: themeColors.text,
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: themeColors.scaffold,
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: themeColors.primary,
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: themeColors.primary,
                },
              }}
            />
            <Typography sx={{ fontSize: "12px", mt: 0.5 }}>
              Allowed Range: {symbol}{sip.minQty} - {symbol}{sip.maxQty}
            </Typography>
          </Box>

          <Box sx={{ mt: 3 }}>
            <Typography sx={{ fontWeight: "bold", mb: 1 }}>
              No. of Months
            </Typography>
            <TextField
              select
              fullWidth
              value={months}
              onChange={(e) => setMonths(e.target.value)}
              SelectProps={{ native: true }}
              sx={{
                borderRadius: "10px",
                "& .MuiOutlinedInput-root": {
                  background: themeColors.mediumContainer,
                  color: themeColors.text,
                },
                "& .MuiInputLabel-root": {
                  color: themeColors.text,
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: themeColors.scaffold,
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: themeColors.primary,
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: themeColors.primary,
                },
              }}
            >
              {Array.from({
                length: sip.maxDuration - sip.minDuration + 1,
              }).map((_, i) => {
                const value = sip.minDuration + i;
                return (
                  <option key={value} value={value}>
                    {value}
                  </option>
                );
              })}
            </TextField>
          </Box>

          {/* Tag */}
          <Box sx={{ mt: 3 }}>
            <Typography sx={{ fontWeight: "bold", mb: 1 }}>
              Tag Your Plan (Optional)
            </Typography>
            <TextField
              fullWidth
              placeholder="Eg: Wedding SIP, Future Plan"
              value={tag}
              onChange={(e) => setTag(e.target.value)}
              sx={{
                borderRadius: "10px",
                "& .MuiOutlinedInput-root": {
                  background: themeColors.mediumContainer,
                  color: themeColors.text,
                },
                "& .MuiInputLabel-root": {
                  color: themeColors.text,
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: themeColors.scaffold,
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: themeColors.primary,
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: themeColors.primary,
                },
              }}
            />
          </Box>

          {/* Disclaimer / Terms */}
          {sip?.discalimer && sip.discalimer.length > 0 && (
            <Box
              sx={{
                mt: 2,
                p: 1,
                borderRadius: 1,
                background: themeColors.mediumContainer,
                color: themeColors.text,
                border: `1px solid ${themeColors.scaffold}`,
              }}
            >
              <Typography sx={{ fontWeight: "bold", mb: 1 }}>
                Terms & Conditions
              </Typography>

              {/* Use a small read more component (you can swap to any other lib you prefer) */}
              <Typography sx={{ fontSize: 13, mb: 1 }}>
                {/* react-read-more-read-less or your own implementation */}
                <ReadMore
                  charLimit={240}
                  readMoreText={"...Show more"}
                  readLessText={" show less"}
                >
                  • {sip.discalimer}
                </ReadMore>
              </Typography>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={agreed}
                    onChange={(e) => setAgreed(e.target.checked)}
                  />
                }
                label={<Typography sx={{ fontSize: 13 }}>I agree</Typography>}
              />
            </Box>
          )}

          {/* Auto-debit box */}
          {sip?.autodebit && (
            <Box
              sx={{
                mt: 2,
                p: 1,
                borderRadius: 1,
                background: themeColors.mediumContainer,
                color: themeColors.text,
                border: `1px solid ${themeColors.scaffold}`,
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={autoDebit}
                    onChange={(e) => setAutoDebit(e.target.checked)}
                    sx={{ color: themeColors.text }}
                  />
                }
                label={
                  <Box>
                    <Typography
                      sx={{ fontWeight: "bold", color: themeColors.text }}
                    >
                      Auto Debit
                    </Typography>
                    <Typography sx={{ color: themeColors.text, fontSize: 13 }}>
                      By opting for Auto Debit, your installments will be auto
                      collected on the selected day.
                    </Typography>
                  </Box>
                }
              />
            </Box>
          )}

          {/* Summary */}
          <Typography sx={{ fontWeight: "bold", mb: 1, mt: 2 }}>
            You will be buying: {calculatedGrams} gms at {symbol}{ratePerGram} = {symbol}
            {totalPayable}
          </Typography>

          {/* Discount display */}
          {discount && (
            <Typography sx={{ mt: 1, fontSize: 14 }}>
              Discounted Price {discount.percentage}% = {symbol}
              {discount.discounted.toFixed(2)}{" "}
              <Typography
                component="span"
                sx={{ color: "text.secondary", ml: 1 }}
              >
                (Original: {symbol}{discount.original.toFixed(2)})
              </Typography>
            </Typography>
          )}

          <Box sx={{ mt: 1 }}>
            {taxtype === "GST" && isSameState && (
              <>
                <Typography variant="body2">
                  CGST: {symbol}{cgst.toFixed(decimalPrecision)}
                </Typography>
                <Typography variant="body2">
                  SGST: {symbol}{sgst.toFixed(decimalPrecision)}
                </Typography>
              </>
            )}

            {taxtype === "GST" && !isSameState && (
              <Typography variant="body2">
                IGST: {symbol}{tax.toFixed(decimalPrecision)}
              </Typography>
            )}

            {taxtype === "VAT" && (
              <>
                <Typography variant="body2">
                  TAX: {symbol}{tax.toFixed(decimalPrecision)}
                </Typography>
                {cess > 0 && (
                  <Typography variant="body2">
                    CESS: {symbol}{cess.toFixed(decimalPrecision)}
                  </Typography>
                )}
              </>
            )}
          </Box>

          {/* Payment Buttons */}
          <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
            {paymentModeEnabled.online && (
              <Button
                fullWidth
                variant="contained"
                sx={{
                  background: themeColors.primary,
                  color: themeColors.text,
                  "&:hover": {
                    background: themeColors.svgColor,
                  },
                  border: `1px solid ${themeColors.scaffold}`,
                  py: 1.2,
                  fontWeight: "bold",
                }}
                onClick={handlePayOnline}
                disabled={loading}
              >
                {loading ? "Processing..." : "Pay Online"}
              </Button>
            )}
            {paymentModeEnabled.offline && !autoDebit && (
              <Button
                fullWidth
                variant="contained"
                sx={{
                  background: themeColors.primary,
                  color: themeColors.text,
                  "&:hover": {
                    background: themeColors.svgColor,
                  },
                  border: `1px solid ${themeColors.scaffold}`,
                  py: 1.2,
                  fontWeight: "bold",
                }}
                onClick={handleOfflineSip}
              >
                Pay Offline
              </Button>
            )}
          </Box>

          <GatewaySelectionDialog
            open={openGatewayDialog}
            onClose={handleGatewaySelect}
            gateways={paymentGateways}
          />

          <Button
            fullWidth
            sx={{
              mt: 2,
              borderRadius: "25px",
              background: themeColors.primary,
              color: themeColors.text,
              "&:hover": {
                background: themeColors.svgColor,
              },
              border: `1px solid ${themeColors.scaffold}`,
              fontWeight: "bold",
            }}
            onClick={() => navigate(-1)}
          >
            Back
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
};

export default SIPSummary;
